import React, { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'clsx';
import Config from 'config';
import fp from 'lodash/fp';
import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell as BaseTableCell,
  TableHead,
  TableRow as BaseTableRow,
  Typography
} from '@material-ui/core';
import { makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import { CheckCircle } from '@material-ui/icons';
import Alert from '@material-ui/lab/Alert';
import useProductCache from 'store/cache/product-cache';
import useUserStore from 'store/user';
import {
  LicenseAction,
  OrganizationLicenseUsage,
  Product,
  ProductCurriculum,
  ProductType
} from 'types/__generated__/graphql';
import { sortProductCurriculumByGrades } from 'utilities/product-utils';
import Popover from '../Popover';
import IntegrationInfoIcon from './IntegrationInfoIcon';
import UnsavedChangeIcon from './UnsavedChangeIcon';

export interface LicenseUpdate {
  productFamilyId: number;
  productCurriculumId: number;
  action: LicenseAction;
}

interface RowData {
  hasChange: boolean;
  isAssigned: boolean;
  isManuallyAssigned: boolean;
  curriculum: ProductCurriculum;
  action: ReactElement | null;
}

interface ProductLicenseTileProps {
  productFamilyId: number;
  productType: ProductType;
  organizationLicenseUsage: OrganizationLicenseUsage[];
  assignedProductCurriculumIds?: number[];
  manuallyAssignedProductCurriculumIds?: number[];
  pendingUpdates?: LicenseUpdate[];
  onLicenseUnassign: (productFamilyId: number, productCurriculumId: number | number[]) => void;
  onLicenseAssign: (productFamilyId: number, productCurriculumId: number | number[]) => void;
}

interface LicenseCountProps {
  product: Product;
  singleProduct?: boolean;
  availableCount: number;
}

const { privilegesGroups } = Config;

const TableRow = withStyles(theme => ({
  root: {
    height: theme.typography.pxToRem(56),
    padding: theme.typography.pxToRem(4),
    border: 'none',
    '&:nth-of-type(odd)': {
      backgroundColor: '#F8F8F8'
    }
  }
}))(BaseTableRow);

const TableHeadCell = withStyles(theme => ({
  root: {
    fontWeight: 400,
    height: theme.typography.pxToRem(32),
    padding: theme.typography.pxToRem(4),
    textTransform: 'uppercase'
  }
}))(BaseTableCell);

const TableCell = withStyles(theme => ({
  root: {
    border: 'none',
    padding: theme.typography.pxToRem(4)
  }
}))(BaseTableCell);

const ProductLicenseCard = ({
  productFamilyId,
  productType,
  organizationLicenseUsage,
  assignedProductCurriculumIds = [],
  manuallyAssignedProductCurriculumIds = [],
  pendingUpdates = [],
  onLicenseAssign,
  onLicenseUnassign
}: ProductLicenseTileProps) => {
  const classes = useStyles();
  const { t } = useTranslation('ProductLicenseCard');
  const [{ privilegesCache }] = useUserStore();
  const [{ findProductsByProductFamilyIdAndType, findProductFamilyById }] = useProductCache();

  const productFamily = findProductFamilyById(productFamilyId);
  const licenseUsage = organizationLicenseUsage.filter(
    x => x.product.family.id === productFamilyId && x.product.type === productType
  );
  const products = fp.flow(
    fp.filter((product: Product) => licenseUsage.some(lu => lu.product.id === product.id)),
    fp.orderBy<Product>('id', ['desc'])
  )(findProductsByProductFamilyIdAndType(productFamilyId, ProductType.USER)) as Product[];

  // Identify whether this user can unassign licenses that were assigned via an integration (i.e. manualAssignment is false)
  // TODO: This should probably be at the product-level, but for now we're allowing it if they have the privlege for *any* product
  const canUnassignAny = privilegesCache.isPrivileged(privilegesGroups.assignOrgLicense);

  const { curriculums, curriculumProductMap } = useMemo(
    () =>
      products.reduce(
        (acc, product) => {
          const productCurriculums = (product.curriculums ? [...product.curriculums] : []).sort(
            sortProductCurriculumByGrades
          );

          productCurriculums.forEach(curriculum =>
            acc.curriculumProductMap.set(curriculum.id, product.id)
          );

          return {
            curriculums: [...acc.curriculums, ...productCurriculums],
            curriculumProductMap: acc.curriculumProductMap
          };
        },
        {
          curriculums: [] as ProductCurriculum[],
          curriculumProductMap: new Map<number, number>()
        }
      ),
    [products]
  );

  // Assign actions subtract from available count, while unassigning adds to it.
  const productAvailableCountMap = products.reduce((acc, product) => {
    const productLicenseUsage = licenseUsage.filter(x => x.product.id === product.id);
    const availableCount =
      fp.sumBy('availableCount', productLicenseUsage) -
      pendingUpdates.filter(
        x =>
          curriculumProductMap.get(x.productCurriculumId) === product.id &&
          x.action === LicenseAction.ASSIGN
      ).length +
      pendingUpdates.filter(
        x =>
          curriculumProductMap.get(x.productCurriculumId) === product.id &&
          x.action === LicenseAction.UNASSIGN
      ).length;

    return acc.set(product.id, availableCount);
  }, new Map<number, number>());

  const assignedCurriculumIds = new Set(assignedProductCurriculumIds);
  const manuallyAssignedCurriculumIds = new Set(manuallyAssignedProductCurriculumIds);
  pendingUpdates.forEach(({ action, productCurriculumId }) => {
    if (action === LicenseAction.ASSIGN) {
      assignedCurriculumIds.add(productCurriculumId);
      manuallyAssignedCurriculumIds.add(productCurriculumId);
    } else {
      assignedCurriculumIds.delete(productCurriculumId);
      manuallyAssignedCurriculumIds.delete(productCurriculumId);
    }
  });

  const handleAssignButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const value = e.currentTarget.value;
    if (value) onLicenseAssign(productFamilyId, +e.currentTarget.value);
  };

  const handleUnassignButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const value = e.currentTarget.value;
    if (value) onLicenseUnassign(productFamilyId, +e.currentTarget.value);
  };

  const showIntegrationInfoPanel =
    !canUnassignAny && assignedCurriculumIds.size > manuallyAssignedCurriculumIds.size;

  const rows: RowData[] = curriculums.map(curriculum => {
    const isAssigned = assignedCurriculumIds.has(curriculum.id);
    const isManuallyAssigned = manuallyAssignedCurriculumIds.has(curriculum.id);
    const availableCount = productAvailableCountMap.get(curriculum.productId) ?? 0;
    const canAssign = availableCount > 0;

    let action: ReactElement | null = null;
    if (isAssigned && !isManuallyAssigned && !canUnassignAny) {
      action = (
        <Popover
          description={t('externalIntegration')}
          icon={<IntegrationInfoIcon className={classes.infoIcon} />}
        />
      );
    } else if (isAssigned) {
      action = (
        <Button
          disableElevation
          aria-label="Unassign license button"
          className={classes.actionButton}
          color="primary"
          size="small"
          variant="contained"
          value={curriculum.id}
          onClick={handleUnassignButtonClick}
        >
          {t('Unassign')}
        </Button>
      );
    } else {
      action = (
        <Button
          disableElevation
          disabled={!canAssign}
          aria-label="Assign license button"
          className={cx(classes.actionButton, {
            [classes.actionButtonDisabled]: !canAssign
          })}
          color="primary"
          size="small"
          variant="contained"
          value={curriculum.id}
          onClick={handleAssignButtonClick}
        >
          {t('Assign')}
        </Button>
      );
    }

    return {
      curriculum,
      hasChange: pendingUpdates.some(p => p.productCurriculumId === curriculum.id),
      isAssigned,
      isManuallyAssigned,
      action
    };
  });

  return (
    <Paper className={classes.root}>
      <Grid container spacing={1} className={classes.container}>
        <Grid item>
          <Box className={classes.updateContainer}>
            {pendingUpdates.length > 0 && <UnsavedChangeIcon />}
          </Box>
          {productFamily?.iconUrl && (
            <img alt="" src={productFamily.iconUrl} className={classes.productIcon} />
          )}
        </Grid>
        <Grid item xs>
          <Typography variant="h3">{productFamily?.name}</Typography>
          <Grid container>
            <Typography variant="subtitle1">
              {t('licensedProductsCount', {
                assignedCount: assignedCurriculumIds.size,
                count: curriculums.length
              })}
            </Typography>
            <Divider
              flexItem
              orientation="vertical"
              variant="middle"
              className={classes.subtitleDivider}
            />
            <Box display="flex" flexDirection="column">
              {products.map(product => (
                <LicenseCount
                  key={`product_${product.id})_license_count`}
                  product={product}
                  availableCount={productAvailableCountMap.get(product.id) ?? 0}
                  singleProduct={products.length === 1}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
        <Grid item container xs={12} spacing={2}>
          {showIntegrationInfoPanel && (
            <Grid item>
              <Alert severity="info" icon={<IntegrationInfoIcon />}>
                {t('externalIntegration')}
              </Alert>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Box paddingTop={2} paddingBottom={2}>
        <Table>
          <TableHead>
            <BaseTableRow>
              <TableHeadCell style={{ width: '40%' }}>{t('Product')}</TableHeadCell>
              <TableHeadCell style={{ width: '25%' }}>{t('Grade')}</TableHeadCell>
              <TableHeadCell style={{ width: '12%' }}>{t('License')}</TableHeadCell>
              <TableHeadCell
                align="center"
                style={{ width: '8%' }}
                className={classes.actionCell}
              />
            </BaseTableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ curriculum, hasChange, isAssigned, isManuallyAssigned, action }) => (
              <TableRow key={curriculum.id}>
                <TableCell>
                  <Grid container spacing={1}>
                    {hasChange && (
                      <Grid item>
                        <UnsavedChangeIcon />
                      </Grid>
                    )}
                    <Grid item>
                      {curriculum.name} ({curriculum.code})
                    </Grid>
                  </Grid>
                </TableCell>
                <TableCell>
                  {curriculum.gradeLevel?.length ? curriculum.gradeLevel?.join(', ') : '-'}
                </TableCell>
                <TableCell>
                  <Grid
                    container
                    alignItems="center"
                    className={cx(
                      classes.licenseStatusContainer,
                      isAssigned && classes.licenseStatusContainerAssigned
                    )}
                  >
                    {isAssigned && (
                      <Grid item className={classes.assignedIconContainer}>
                        <CheckCircle className={classes.assignedIcon} />
                      </Grid>
                    )}
                    <Grid item>{isAssigned ? t('Assigned') : t('Unassigned')}</Grid>
                  </Grid>
                </TableCell>
                <TableCell className={classes.actionCell}>
                  <div className={classes.actionContainer}>{action}</div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
};

const LicenseCount = ({ product, availableCount, singleProduct = false }: LicenseCountProps) => {
  const { t } = useTranslation('ProductLicenseCard');

  return (
    <Typography key={`product_${product.id}_count`} variant="subtitle1">
      {t(singleProduct ? 'availableLicenses' : 'availableProductLicenses', {
        count: availableCount,
        productName: product.name
      })}
    </Typography>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2)
  },
  container: { position: 'relative' },
  productIcon: {
    padding: theme.spacing(1),
    height: theme.typography.pxToRem(80),
    width: theme.typography.pxToRem(80)
  },
  updateContainer: {
    position: 'absolute',
    top: 0,
    left: 0
  },
  infoIcon: {
    cursor: 'pointer',
    fontSize: theme.typography.pxToRem(24)
  },
  assignedIcon: {
    fontSize: theme.typography.pxToRem(18)
  },
  actionCell: {
    width: 75
  },
  actionContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  licenseStatusContainer: {
    lineHeight: '1rem'
  },
  licenseStatusContainerAssigned: {
    fill: theme.palette.success.dark,
    color: theme.palette.success.dark
  },
  assignedIconContainer: {
    paddingRight: theme.typography.pxToRem(4)
  },
  actionButton: {
    background: '#FFFFFF',
    border: '1px solid #969696',
    color: '#2D2D2D',
    padding: theme.typography.pxToRem(20),
    '&:hover': {
      background: '#FFFFFF'
    }
  },
  actionButtonDisabled: {
    backgroundColor: '#FFFFFF !important',
    boxShadow: 'none !important',
    color: 'rgba(0, 0, 0, 0.26) !important'
  },
  subtitleDivider: {
    width: 1,
    backgroundColor: theme.palette.text.secondary
  }
}));

export default ProductLicenseCard;
